import React, { useEffect, useState } from "react";

import { getCancelJobRequest } from "@api/job/cancelJob";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Typography,
  Box,
  IconButton,
  Collapse,
  Stack,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";

import { handleApiError } from "@features/config";

import { ReactComponent as CancelRequestIcon } from "@assets/icons/cancel_request.svg";

import { useSnackbar } from "@contexts/SnackbarContext";

import CancelRequestCard from "./CancelRequestCard";

type CancelRequestType = {
  jobId: string;
};
const CancelRequests: React.FC<CancelRequestType> = ({ jobId }) => {
  const [open, setOpen] = useState(true);
  const {
    palette: { common },
  } = useTheme();
  const { setSnackbar } = useSnackbar();
  const {
    data: listCancelJobRequest,
    error,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryFn: () => getCancelJobRequest(jobId),
    queryKey: ["getCancelJobRequestList", jobId],
    enabled: !!jobId,
  });

  if (error) {
    setSnackbar({
      message: handleApiError(error).message,
      open: true,
      severity: "error",
    });
  }

  useEffect(() => {
    refetch();
  }, [jobId]);

  if (isFetching || isLoading) {
    return (
      <Stack
        sx={{
          py: 1,
          bgcolor: common.white,
          alignItems: "center",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return listCancelJobRequest?.length ? (
    <Box
      sx={{
        py: 1,
        bgcolor: common.white,
        zIndex: 1,
        width: "100%",
      }}
    >
      <Stack px={2} direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          color={common.mediumText}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          <CancelRequestIcon /> Cancel Requests
        </Typography>
        <IconButton onClick={() => setOpen(!open)}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>
      <Collapse in={open}>
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 2,
            px: 2,
            pb: 1,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollBehavior: "smooth",
            paddingRight: "10px",
          }}
        >
          {listCancelJobRequest.map((request, index) => (
            <CancelRequestCard key={index} {...request} jobId={jobId} />
          ))}
        </Box>
      </Collapse>
    </Box>
  ) : null;
};

export default CancelRequests;
